import type {
  InspectWorkforceInvitationResult,
  WorkforceInvitationActivationResult,
  WorkforceInvitationStatus,
  WorkforcePhoneChangeResult,
  WorkforceWechatRebindResult,
} from "@billiard/contracts";
import { ShieldCheck } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import {
  ApiRequestError,
  activateWorkforceInvitation,
  bindWorkforceInvitationWechat,
  completeWorkforcePhoneChange,
  completeWorkforceWechatRebind,
  confirmWorkforceInvitationIdentity,
  inspectWorkforceInvitation,
  requestWorkforcePhoneChangeNewPhoneOtp,
  verifyWorkforceInvitationOtp,
  verifyWorkforcePhoneChangeNewPhoneOtp,
} from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  clearWorkforceInvitationActivationRecovery,
  createWorkforceInvitationActivationRecovery,
  ensureWorkforceInvitationActivationRecovery,
  loadWorkforceInvitationActivationRecovery,
  readWorkforceInvitationTokenFromHash,
  saveWorkforceInvitationActivationRecovery,
  type WorkforceInvitationActivationRecovery,
} from "./workforceInvitationActivationRecovery";
import {
  clearWorkforcePhoneChangeActivationRecovery,
  ensureWorkforcePhoneChangeActivationRecovery,
  loadOrCreateWorkforcePhoneChangeActivationRecovery,
  saveWorkforcePhoneChangeActivationRecovery,
  type WorkforcePhoneChangeActivationRecovery,
} from "./workforcePhoneChangeActivationRecovery";

const roleLabels = {
  MANAGER: "店长",
  CASHIER: "收银员",
  WAITER: "服务员",
  COACH: "助教",
} as const;

interface InitialActivationState {
  recovery: WorkforceInvitationActivationRecovery | null;
  error: string | null;
  resumed: boolean;
}

function initializeActivationState(): InitialActivationState {
  if (typeof window === "undefined") {
    return { recovery: null, error: "当前环境不能安全读取邀请链接", resumed: false };
  }
  try {
    const token = readWorkforceInvitationTokenFromHash(window.location.hash);
    if (token) {
      const recovery = saveWorkforceInvitationActivationRecovery(
        window.sessionStorage,
        createWorkforceInvitationActivationRecovery(token),
      );
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      return { recovery, error: null, resumed: false };
    }
    const recovery = loadWorkforceInvitationActivationRecovery(window.sessionStorage);
    if (recovery) return { recovery, error: null, resumed: true };
    return {
      recovery: null,
      error: "邀请链接缺少有效凭证，请让老板或店长重新发送邀请",
      resumed: false,
    };
  } catch (error) {
    return {
      recovery: null,
      error: businessErrorMessage(error, "当前标签页无法安全保存邀请进度"),
      resumed: false,
    };
  }
}

function simulatedEvidenceMatches(inspection: InspectWorkforceInvitationResult): boolean {
  return inspection.providerMode === "SIMULATED"
    ? inspection.evidence === "PROVIDER_SIMULATED"
    : inspection.evidence === "PROVIDER_ACCEPTED";
}

function resumableStateIsComplete(
  status: WorkforceInvitationStatus,
  recovery: WorkforceInvitationActivationRecovery,
  phoneRecovery: WorkforcePhoneChangeActivationRecovery | null = null,
): boolean {
  if (status === "OTP_PENDING") return recovery.otpChallengeId !== null;
  if (status === "OTP_VERIFIED") {
    return phoneRecovery !== null || recovery.wechatChallengeId !== null;
  }
  if (status === "NEW_PHONE_OTP_PENDING") {
    return (
      phoneRecovery !== null &&
      phoneRecovery.newPhone !== null &&
      ((phoneRecovery.maskedNewPhone !== null && phoneRecovery.newPhoneOtpChallengeId !== null) ||
        (phoneRecovery.maskedNewPhone === null && phoneRecovery.newPhoneOtpChallengeId === null))
    );
  }
  if (status === "PHONE_CHANGE_READY") {
    return (
      phoneRecovery !== null &&
      phoneRecovery.newPhone !== null &&
      phoneRecovery.maskedNewPhone !== null &&
      phoneRecovery.newPhoneOtpChallengeId !== null
    );
  }
  return true;
}

const phoneInputPattern = /^[0-9+\s()（）._:/／-]{6,40}$/u;

function comparablePhone(value: string): string {
  return value.trim().replace(/[\s()（）._:/／-]/gu, "");
}

function phoneChangeInputError(newPhone: string, currentPhone: string): string | null {
  const normalized = newPhone.trim();
  if (!phoneInputPattern.test(normalized)) {
    return "请输入可接收短信的新手机号，只能使用数字及常见电话分隔符，长度为 6 到 40 个字符。";
  }
  if (
    currentPhone.trim().length > 0 &&
    comparablePhone(normalized) === comparablePhone(currentPhone)
  ) {
    return "新手机号不能与当前登录手机号相同，请填写另一个可接收短信的号码。";
  }
  return null;
}

function phoneChangeStepErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  switch (error.code) {
    case "WORKFORCE_PHONE_CHANGE_SAME_PHONE":
      return "新手机号不能与当前登录手机号相同，请填写另一个可接收短信的号码。";
    case "WORKFORCE_PHONE_CHANGE_NEW_PHONE_INVALID":
      return "新手机号格式不正确，请填写可接收短信的完整号码后重试。";
    case "WORKFORCE_PHONE_CHANGE_NEW_PHONE_OTP_INVALID":
      return "新手机号验证码不正确，请重新输入新手机号刚刚收到的 6 位验证码。";
    case "WORKFORCE_PHONE_CHANGE_NEW_PHONE_OTP_EXPIRED":
      return "新手机号验证码已过期，请重新填写新手机号并获取一条新验证码。";
    case "WORKFORCE_INVITATION_LOCKED":
      return "验证尝试次数过多，手机号更换已锁定，请返回“我的资料”重新发起。";
    default:
      return null;
  }
}

function publicPhoneChangeStepIsOutcomeUnknown(error: unknown): boolean {
  return (
    !(error instanceof ApiRequestError) ||
    error.statusCode === undefined ||
    error.statusCode === 408 ||
    error.statusCode >= 500 ||
    error.code === "GATEWAY_RESPONSE_INVALID"
  );
}

type WorkforceInvitationUnknownCommand =
  | { kind: "IDENTITY"; displayName: string; phone: string }
  | { kind: "OTP"; otpCode: string }
  | { kind: "WECHAT"; authorizationCode: string }
  | { kind: "ACTIVATE"; backupPassword: string };

export function WorkforceInvitationActivationPage(): React.JSX.Element {
  const titleId = useId();
  const initial = useRef<InitialActivationState | null>(null);
  if (initial.current === null) initial.current = initializeActivationState();
  const [recovery, setRecovery] = useState(initial.current.recovery);
  const [resumed] = useState(initial.current.resumed);
  const [inspection, setInspection] = useState<InspectWorkforceInvitationResult | null>(null);
  const [status, setStatus] = useState<WorkforceInvitationStatus | null>(null);
  const [activation, setActivation] = useState<WorkforceInvitationActivationResult | null>(null);
  const [wechatRebindResult, setWechatRebindResult] = useState<WorkforceWechatRebindResult | null>(
    null,
  );
  const [phoneChangeResult, setPhoneChangeResult] = useState<WorkforcePhoneChangeResult | null>(
    null,
  );
  const [phoneChangeRecovery, setPhoneChangeRecovery] =
    useState<WorkforcePhoneChangeActivationRecovery | null>(null);
  const [localCompletionRequired, setLocalCompletionRequired] = useState(false);
  const [error, setError] = useState<string | null>(initial.current.error);
  const [busy, setBusy] = useState<string | null>(null);
  const flightRef = useRef<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPhoneOtpCode, setNewPhoneOtpCode] = useState("");
  const [authorizationCode, setAuthorizationCode] = useState("");
  const [backupPassword, setBackupPassword] = useState("");
  const [backupPasswordConfirm, setBackupPasswordConfirm] = useState("");
  const [backupPasswordVisible, setBackupPasswordVisible] = useState(false);
  const [backupPasswordConfirmVisible, setBackupPasswordConfirmVisible] = useState(false);
  const [unknownCommand, setUnknownCommand] = useState<WorkforceInvitationUnknownCommand | null>(
    null,
  );
  const backupPasswordId = useId();
  const backupPasswordConfirmId = useId();
  const simulation = inspection?.providerMode === "SIMULATED";
  const passwordReset = inspection?.purpose === "PASSWORD_RESET";
  const wechatRebind = inspection?.purpose === "WECHAT_REBIND";
  const phoneChange = inspection?.purpose === "PHONE_CHANGE";
  const accountRecovery = passwordReset || wechatRebind || phoneChange;

  const persist = (
    current: WorkforceInvitationActivationRecovery,
    patch: Partial<WorkforceInvitationActivationRecovery>,
  ): WorkforceInvitationActivationRecovery => {
    const next = saveWorkforceInvitationActivationRecovery(window.sessionStorage, {
      ...current,
      ...patch,
    });
    setRecovery(next);
    return next;
  };

  const persistPhoneChange = (
    current: WorkforcePhoneChangeActivationRecovery,
    patch: Partial<WorkforcePhoneChangeActivationRecovery>,
  ): WorkforcePhoneChangeActivationRecovery => {
    const next = saveWorkforcePhoneChangeActivationRecovery(window.sessionStorage, {
      ...current,
      ...patch,
    });
    setPhoneChangeRecovery(next);
    return next;
  };

  const runInvitationCommand = async <T,>(
    command: WorkforceInvitationUnknownCommand,
    action: () => Promise<T>,
  ): Promise<T> => {
    try {
      const result = await action();
      setUnknownCommand(null);
      return result;
    } catch (cause) {
      setUnknownCommand(publicPhoneChangeStepIsOutcomeUnknown(cause) ? command : null);
      throw cause;
    }
  };

  useEffect(() => {
    const currentRecovery = initial.current?.recovery ?? null;
    if (!currentRecovery || initial.current?.error) return;
    let active = true;
    void inspectWorkforceInvitation({ activationToken: currentRecovery.activationToken })
      .then((result) => {
        if (!active) return;
        if (!simulatedEvidenceMatches(result)) {
          throw new Error(
            result.purpose === "PASSWORD_RESET"
              ? "密码恢复渠道与验证证据不一致"
              : result.purpose === "WECHAT_REBIND"
                ? "微信换绑渠道与验证证据不一致"
                : result.purpose === "PHONE_CHANGE"
                  ? "手机号更换渠道与验证证据不一致"
                  : "邀请渠道与验证证据不一致",
          );
        }
        if (currentRecovery.invitationId && currentRecovery.invitationId !== result.invitationId) {
          throw new Error(
            result.purpose === "PASSWORD_RESET"
              ? "当前标签页保存的密码恢复与服务端返回不是同一次恢复"
              : result.purpose === "WECHAT_REBIND"
                ? "当前标签页保存的微信换绑与服务端返回不是同一次换绑"
                : result.purpose === "PHONE_CHANGE"
                  ? "当前标签页保存的手机号更换与服务端返回不是同一次更换"
                  : "当前标签页保存的邀请与服务端返回不是同一份邀请",
          );
        }
        const confirmed = currentRecovery.invitationId
          ? currentRecovery
          : saveWorkforceInvitationActivationRecovery(window.sessionStorage, {
              ...currentRecovery,
              invitationId: result.invitationId,
            });
        if (confirmed !== currentRecovery) setRecovery(confirmed);
        const confirmedPhoneRecovery =
          result.purpose === "PHONE_CHANGE"
            ? loadOrCreateWorkforcePhoneChangeActivationRecovery(
                window.sessionStorage,
                confirmed.activationToken,
                result.invitationId,
              )
            : null;
        if (confirmedPhoneRecovery) {
          setPhoneChangeRecovery(confirmedPhoneRecovery);
          setNewPhone(confirmedPhoneRecovery.newPhone ?? "");
          setNewPhoneOtpCode(confirmedPhoneRecovery.simulatedNewPhoneOtpCode ?? "");
        }
        if (!resumableStateIsComplete(result.status, confirmed, confirmedPhoneRecovery)) {
          throw new Error(
            result.purpose === "PASSWORD_RESET"
              ? "当前标签页缺少继续原密码恢复所需的验证记录，请重新获取密码恢复链接"
              : result.purpose === "WECHAT_REBIND"
                ? "当前标签页缺少继续原微信换绑所需的验证记录，请重新发起微信换绑"
                : result.purpose === "PHONE_CHANGE"
                  ? "当前标签页缺少继续原手机号更换所需的验证记录，请重新发起手机号更换"
                  : "当前标签页缺少继续原邀请所需的验证记录，请重新获取邀请链接",
          );
        }
        const requiresLocalCompletion =
          result.status === "ACTIVE" && result.localCompletionRequired === true;
        setLocalCompletionRequired(requiresLocalCompletion);
        if (
          (!requiresLocalCompletion && result.status === "ACTIVE") ||
          ["DELIVERY_FAILED", "EXPIRED", "CANCELLED", "LOCKED"].includes(result.status)
        ) {
          clearWorkforceInvitationActivationRecovery(window.sessionStorage);
          if (result.purpose === "PHONE_CHANGE") {
            clearWorkforcePhoneChangeActivationRecovery(window.sessionStorage);
            setPhoneChangeRecovery(null);
          }
          setRecovery(null);
        }
        setInspection(result);
        setDisplayName(result.displayName);
        setOtpCode(confirmed.simulatedOtpCode ?? "");
        setAuthorizationCode(confirmed.simulatedAuthorizationCode ?? "");
        setStatus(result.status);
      })
      .catch((cause) => {
        if (active) setError(businessErrorMessage(cause, "邀请状态暂时无法确认"));
      });
    return () => {
      active = false;
    };
  }, []);

  const runOnce = async (kind: string, action: () => Promise<void>): Promise<void> => {
    if (flightRef.current !== null) return;
    flightRef.current = kind;
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (cause) {
      const message =
        (kind === "ACTIVATE" &&
        cause instanceof ApiRequestError &&
        cause.code === "IDEMPOTENCY_COLLISION"
          ? passwordReset
            ? "这次输入与原新密码不同，系统没有再次修改账号。请填写上次提交的同一新密码，继续确认原密码重置；不要另开操作。"
            : "这次输入与原备用密码不同，系统没有再次修改账号。请填写上次提交的同一备用密码，继续确认原账号启用；不要重新设置或另开操作。"
          : null) ??
        (phoneChange ? phoneChangeStepErrorMessage(cause) : null) ??
        businessErrorMessage(cause, "本步没有完成，请保留页面后重试");
      setError(
        passwordReset
          ? message.replaceAll("邀请", "密码恢复")
          : wechatRebind
            ? message.replaceAll("邀请", "微信换绑")
            : phoneChange
              ? message.replaceAll("邀请", "手机号更换")
              : message,
      );
    } finally {
      flightRef.current = null;
      setBusy(null);
    }
  };

  const roleText = inspection?.roles.map((role) => roleLabels[role]).join("、") ?? "";
  const newPhoneError = phoneChangeInputError(newPhone, phone);
  const workflowLabel = passwordReset
    ? "密码恢复"
    : wechatRebind
      ? "微信换绑"
      : phoneChange
        ? "手机号更换"
        : "邀请";
  const terminalMessage =
    status === "EXPIRED"
      ? `${workflowLabel}链接已过期，请${wechatRebind ? "重新发起微信换绑" : phoneChange ? "重新发起手机号更换" : "让老板或店长重新发送"}`
      : status === "CANCELLED"
        ? `${workflowLabel}已取消，请联系门店管理人员`
        : status === "LOCKED"
          ? phoneChange
            ? "验证尝试次数过多，手机号更换已锁定，请返回“我的资料”重新发起。"
            : `验证尝试次数过多，${workflowLabel}已锁定，请联系门店管理人员`
          : status === "DELIVERY_FAILED"
            ? `${workflowLabel}链接发送失败，请${wechatRebind ? "重新发起微信换绑" : phoneChange ? "重新发起手机号更换" : "让老板或店长重新发送"}`
            : null;

  return (
    <main className="auth-screen workforce-invitation-activation-screen">
      <section className="auth-panel" aria-labelledby={titleId}>
        <div className="auth-title">
          <ShieldCheck size={28} aria-hidden="true" />
          <div>
            <h1 id={titleId}>
              {inspection
                ? passwordReset
                  ? "员工密码恢复"
                  : wechatRebind
                    ? "员工微信换绑"
                    : phoneChange
                      ? "员工登录手机号更换"
                      : "员工账号邀请"
                : "员工身份验证"}
            </h1>
            <p>
              {inspection
                ? passwordReset
                  ? "按顺序确认现有账号本人、手机号、微信并设置新密码"
                  : wechatRebind
                    ? "按顺序确认现有账号本人、旧手机号和新微信，不修改密码"
                    : phoneChange
                      ? "按顺序核对本人和当前手机号、验证旧号、验证新号，再确认更换"
                      : "按顺序确认本人身份、手机、微信和备用密码"
                : "正在确认这次验证的用途和当前进度"}
            </p>
          </div>
        </div>

        {simulation ? (
          <div className="warning-band" aria-live="polite">
            <strong>仅内部模拟</strong>
            <span>
              {phoneChange
                ? "当前手机号与新手机号的短信步骤只验证软件流程，不代表真实短信渠道已经开通。"
                : "短信和微信步骤只验证软件流程，不代表真实短信或微信渠道已经开通。"}
            </span>
          </div>
        ) : null}
        {resumed && recovery && inspection ? (
          <div className="form-hint" aria-live="polite">
            {passwordReset
              ? "已从当前标签页恢复上次密码恢复进度，请继续原操作，不要更换链接。"
              : wechatRebind
                ? "已从当前标签页恢复上次微信换绑进度，请继续原操作，不要更换链接。"
                : phoneChange
                  ? "已从当前标签页恢复上次手机号更换进度，请继续原操作，不要更换号码或链接。"
                  : "已从当前标签页恢复上次邀请进度，请继续原操作，不要换另一份邀请。"}
          </div>
        ) : null}
        {inspection ? (
          <div className="form-hint">
            {passwordReset
              ? "恢复账号"
              : wechatRebind
                ? "换绑账号"
                : phoneChange
                  ? "更换账号"
                  : "受邀人"}
            ：{inspection.displayName}；{accountRecovery ? "现有角色" : "拟开通角色"}：{roleText}；
            {passwordReset
              ? "恢复链接"
              : wechatRebind
                ? "换绑链接"
                : phoneChange
                  ? "更换链接"
                  : "邀请"}
            有效期至
            {new Date(inspection.expiresAt).toLocaleString("zh-CN")}。
          </div>
        ) : null}
        {inspection && !accountRecovery ? (
          <div className="form-hint" role="note">
            这是一份 24 小时内有效的一次性邀请；启用、取消、过期或重发后，旧链接都不能再次使用。
          </div>
        ) : null}
        {inspection?.roles.includes("COACH") && !accountRecovery ? (
          <div className="form-hint" role="note">
            这是助教的新账号邀请。如您以前有已停用的旧账号，完成本邀请也不会自动恢复那个旧账号。
          </div>
        ) : null}
        {passwordReset ? (
          <div className="warning-band" role="note">
            <strong>这是现有账号的密码恢复，不会新建账号。</strong>
            <span>
              本人手机号必须与现有账号匹配，随后需要重新确认微信。完成后所有旧设备都会退出；账号资金、提成和业务历史保持不变。
            </span>
          </div>
        ) : null}
        {wechatRebind ? (
          <div className="warning-band" role="note">
            <strong>这是现有账号的微信换绑，不会新建账号，也不会修改密码。</strong>
            <span>
              本人姓名和旧手机号必须与现有账号匹配。确认完成后旧微信会被新微信替换，所有旧设备都需要重新登录；账号资金、提成和业务历史保持不变。
            </span>
          </div>
        ) : null}
        {phoneChange ? (
          <div className="warning-band" role="note">
            <strong>这是本人登录手机号更换，不会新建账号。</strong>
            <span>
              先核对现有姓名和当前登录手机号，再分别验证当前手机号与新手机号。成功后所有旧设备都会退出；不会转移或删除会员余额、订单、提成和历史记录。
            </span>
          </div>
        ) : null}
        {!inspection && recovery && !error ? (
          <div className="auth-loading" aria-live="polite">
            正在安全核对验证链接…
          </div>
        ) : null}
        {error ? (
          <div className="error-band" role="alert" tabIndex={-1}>
            {error}
          </div>
        ) : null}
        {unknownCommand ? (
          <output className="warning-band">
            {unknownCommand.kind === "ACTIVATE"
              ? "账号启用结果尚未确认。页面已锁定原备用密码和原操作号；只能继续确认原账号启用，不能修改密码或换一份邀请。"
              : "上一步结果尚未确认。页面已锁定原填写内容和原操作号；只能继续确认原操作，不能改填或换一份邀请。"}
          </output>
        ) : null}
        {terminalMessage ? (
          <div className="error-band" role="alert">
            {terminalMessage}
          </div>
        ) : null}

        {status === "PENDING_IDENTITY_CONFIRMATION" && recovery && inspection ? (
          <form
            name="workforce-invitation-identity"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              const fixedCommand =
                unknownCommand?.kind === "IDENTITY"
                  ? unknownCommand
                  : {
                      kind: "IDENTITY" as const,
                      displayName: displayName.trim(),
                      phone: phone.trim(),
                    };
              if (fixedCommand.displayName.length < 1 || fixedCommand.phone.length < 6) return;
              void runOnce("IDENTITY", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                const result = await runInvitationCommand(fixedCommand, () =>
                  confirmWorkforceInvitationIdentity(
                    {
                      activationToken: recovery.activationToken,
                      displayName: fixedCommand.displayName,
                      phone: fixedCommand.phone,
                      idempotencyKey: recovery.identityIdempotencyKey,
                    },
                    inspection.invitationId,
                  ),
                );
                persist(recovery, {
                  invitationId: result.invitationId,
                  otpChallengeId: result.challengeId,
                  simulatedOtpCode: result.simulatedOtpCode,
                });
                setOtpCode(result.simulatedOtpCode ?? "");
                setStatus(result.status);
              });
            }}
          >
            <h2>{accountRecovery ? "确认现有账号本人身份" : "确认本人身份"}</h2>
            <label>
              {accountRecovery ? "现有账号姓名" : "本人姓名"}
              <input
                name="invitation-display-name"
                value={displayName}
                maxLength={80}
                autoComplete="name"
                disabled={busy !== null || unknownCommand?.kind === "IDENTITY"}
                onChange={(event) => setDisplayName(event.currentTarget.value)}
              />
            </label>
            <label>
              {passwordReset
                ? "现有账号绑定手机号"
                : wechatRebind
                  ? "现有账号旧手机号"
                  : phoneChange
                    ? "当前登录手机号"
                    : "本人手机号"}
              <input
                name="invitation-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                maxLength={40}
                autoComplete="tel"
                disabled={busy !== null || unknownCommand?.kind === "IDENTITY"}
                onChange={(event) => setPhone(event.currentTarget.value)}
              />
            </label>
            {accountRecovery ? <p>手机号必须与这个现有账号一致；不匹配时不会发送验证码。</p> : null}
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "IDENTITY"
                ? "正在确认…"
                : unknownCommand?.kind === "IDENTITY"
                  ? "继续确认原身份资料"
                  : accountRecovery
                    ? "确认手机号并发送验证码"
                    : "确认身份并发送验证码"}
            </button>
          </form>
        ) : null}

        {status === "OTP_PENDING" && recovery?.otpChallengeId && inspection ? (
          <form
            name="workforce-invitation-otp"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              const fixedCommand =
                unknownCommand?.kind === "OTP" ? unknownCommand : { kind: "OTP" as const, otpCode };
              if (!/^\d{6}$/.test(fixedCommand.otpCode)) return;
              void runOnce("OTP", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                let result: Awaited<ReturnType<typeof verifyWorkforceInvitationOtp>>;
                try {
                  result = await runInvitationCommand(fixedCommand, () =>
                    verifyWorkforceInvitationOtp(
                      {
                        activationToken: recovery.activationToken,
                        challengeId: recovery.otpChallengeId as string,
                        otpCode: fixedCommand.otpCode,
                        idempotencyKey: recovery.otpIdempotencyKey,
                      },
                      inspection.invitationId,
                    ),
                  );
                } catch (cause) {
                  if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_INVITATION_OTP_INVALID"
                  ) {
                    persist(recovery, {
                      otpIdempotencyKey: `workforce-invite-otp-${crypto.randomUUID()}`,
                    });
                  } else if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_INVITATION_OTP_EXPIRED"
                  ) {
                    persist(recovery, {
                      identityIdempotencyKey: `workforce-invite-identity-${crypto.randomUUID()}`,
                      otpChallengeId: null,
                      simulatedOtpCode: null,
                      otpIdempotencyKey: `workforce-invite-otp-${crypto.randomUUID()}`,
                    });
                    setOtpCode("");
                    setStatus("PENDING_IDENTITY_CONFIRMATION");
                  } else if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_INVITATION_LOCKED"
                  ) {
                    clearWorkforceInvitationActivationRecovery(window.sessionStorage);
                    if (phoneChange) {
                      clearWorkforcePhoneChangeActivationRecovery(window.sessionStorage);
                      setPhoneChangeRecovery(null);
                    }
                    setRecovery(null);
                    setStatus("LOCKED");
                  }
                  throw cause;
                }
                if ("challengeId" in result) {
                  persist(recovery, {
                    invitationId: result.invitationId,
                    wechatChallengeId: result.challengeId,
                    simulatedAuthorizationCode: result.simulatedAuthorizationCode,
                  });
                  setAuthorizationCode(result.simulatedAuthorizationCode ?? "");
                } else {
                  persist(recovery, {
                    invitationId: result.invitationId,
                    wechatChallengeId: null,
                    simulatedAuthorizationCode: null,
                  });
                  setAuthorizationCode("");
                }
                setStatus(result.status);
              });
            }}
          >
            <h2>
              {passwordReset
                ? "输入密码恢复短信验证码"
                : wechatRebind
                  ? "输入微信换绑短信验证码"
                  : phoneChange
                    ? "输入当前手机号验证码"
                    : "输入短信验证码"}
            </h2>
            {simulation ? <p>内部模拟验证码已自动填入；真实短信渠道尚未验证。</p> : null}
            <label>
              6 位验证码
              <input
                name="invitation-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otpCode}
                maxLength={6}
                disabled={busy !== null || unknownCommand?.kind === "OTP"}
                onChange={(event) => setOtpCode(event.currentTarget.value.replace(/\D/g, ""))}
              />
            </label>
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "OTP"
                ? "正在验证…"
                : unknownCommand?.kind === "OTP"
                  ? "继续确认原短信验证码"
                  : "验证短信验证码"}
            </button>
          </form>
        ) : null}

        {(status === "OTP_VERIFIED" ||
          (status === "NEW_PHONE_OTP_PENDING" &&
            phoneChangeRecovery?.newPhone !== null &&
            phoneChangeRecovery?.newPhoneOtpChallengeId === null)) &&
        phoneChange &&
        recovery &&
        phoneChangeRecovery &&
        inspection ? (
          <form
            name="workforce-phone-change-new-phone"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (newPhoneError && phoneChangeRecovery.newPhone === null) {
                setError(newPhoneError);
                return;
              }
              void runOnce("PHONE_CHANGE_NEW_PHONE_REQUEST", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                ensureWorkforcePhoneChangeActivationRecovery(
                  window.sessionStorage,
                  phoneChangeRecovery,
                );
                const dispatched =
                  phoneChangeRecovery.newPhone === null
                    ? persistPhoneChange(phoneChangeRecovery, {
                        newPhone: newPhone.trim(),
                      })
                    : phoneChangeRecovery;
                try {
                  const result = await requestWorkforcePhoneChangeNewPhoneOtp(
                    {
                      activationToken: recovery.activationToken,
                      newPhone: dispatched.newPhone as string,
                      idempotencyKey: dispatched.requestIdempotencyKey,
                    },
                    inspection,
                  );
                  persistPhoneChange(dispatched, {
                    maskedNewPhone: result.maskedNewPhone,
                    newPhoneOtpChallengeId: result.challengeId,
                    simulatedNewPhoneOtpCode: result.simulatedOtpCode,
                  });
                  setNewPhoneOtpCode(result.simulatedOtpCode ?? "");
                  setStatus(result.status);
                } catch (cause) {
                  if (!publicPhoneChangeStepIsOutcomeUnknown(cause)) {
                    persistPhoneChange(dispatched, {
                      newPhone: null,
                      maskedNewPhone: null,
                      newPhoneOtpChallengeId: null,
                      simulatedNewPhoneOtpCode: null,
                      requestIdempotencyKey: `workforce-phone-change-new-phone-request-${crypto.randomUUID()}`,
                    });
                    setNewPhone("");
                  }
                  throw cause;
                }
              });
            }}
          >
            <h2>填写并验证新手机号</h2>
            <p>
              当前手机号已经通过验证。请输入今后用于登录的新手机号；系统会向这个新号码发送另一条验证码。
            </p>
            <label>
              新登录手机号
              <input
                name="workforce-phone-change-new-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneChangeRecovery.newPhone ?? newPhone}
                maxLength={40}
                disabled={busy !== null || phoneChangeRecovery.newPhone !== null}
                onChange={(event) => setNewPhone(event.currentTarget.value)}
              />
            </label>
            {phoneChangeRecovery.newPhone === null && newPhone.length > 0 && newPhoneError ? (
              <div className="form-hint" role="alert">
                {newPhoneError}
              </div>
            ) : null}
            {phoneChangeRecovery.newPhone !== null ? (
              <output className="warning-band">
                上次发送结果尚未确认，已锁定原新手机号和请求编号。只能继续确认原请求，不能改号码。
              </output>
            ) : null}
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "PHONE_CHANGE_NEW_PHONE_REQUEST"
                ? "正在发送新号验证码…"
                : phoneChangeRecovery.newPhone !== null
                  ? "使用原请求继续确认"
                  : "向新手机号发送验证码"}
            </button>
          </form>
        ) : null}

        {status === "NEW_PHONE_OTP_PENDING" &&
        phoneChange &&
        recovery &&
        phoneChangeRecovery?.newPhoneOtpChallengeId &&
        inspection ? (
          <form
            name="workforce-phone-change-new-phone-otp"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!/^\d{6}$/u.test(newPhoneOtpCode)) {
                setError("请输入新手机号收到的 6 位验证码。");
                return;
              }
              void runOnce("PHONE_CHANGE_NEW_PHONE_VERIFY", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                ensureWorkforcePhoneChangeActivationRecovery(
                  window.sessionStorage,
                  phoneChangeRecovery,
                );
                try {
                  const result = await verifyWorkforcePhoneChangeNewPhoneOtp(
                    {
                      activationToken: recovery.activationToken,
                      challengeId: phoneChangeRecovery.newPhoneOtpChallengeId as string,
                      otpCode: newPhoneOtpCode,
                      idempotencyKey: phoneChangeRecovery.verifyIdempotencyKey,
                    },
                    inspection,
                  );
                  persistPhoneChange(phoneChangeRecovery, {
                    maskedNewPhone: result.maskedNewPhone,
                    simulatedNewPhoneOtpCode: null,
                  });
                  setNewPhoneOtpCode("");
                  setStatus(result.status);
                } catch (cause) {
                  if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_PHONE_CHANGE_NEW_PHONE_OTP_INVALID"
                  ) {
                    persistPhoneChange(phoneChangeRecovery, {
                      simulatedNewPhoneOtpCode: null,
                      verifyIdempotencyKey: `workforce-phone-change-new-phone-verify-${crypto.randomUUID()}`,
                    });
                    setNewPhoneOtpCode("");
                  } else if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_PHONE_CHANGE_NEW_PHONE_OTP_EXPIRED"
                  ) {
                    persistPhoneChange(phoneChangeRecovery, {
                      newPhone: null,
                      maskedNewPhone: null,
                      newPhoneOtpChallengeId: null,
                      simulatedNewPhoneOtpCode: null,
                      requestIdempotencyKey: `workforce-phone-change-new-phone-request-${crypto.randomUUID()}`,
                      verifyIdempotencyKey: `workforce-phone-change-new-phone-verify-${crypto.randomUUID()}`,
                    });
                    setNewPhone("");
                    setNewPhoneOtpCode("");
                    setStatus("OTP_VERIFIED");
                  } else if (
                    cause instanceof ApiRequestError &&
                    cause.code === "WORKFORCE_INVITATION_LOCKED"
                  ) {
                    clearWorkforceInvitationActivationRecovery(window.sessionStorage);
                    clearWorkforcePhoneChangeActivationRecovery(window.sessionStorage);
                    setRecovery(null);
                    setPhoneChangeRecovery(null);
                    setStatus("LOCKED");
                  }
                  throw cause;
                }
              });
            }}
          >
            <h2>输入新手机号验证码</h2>
            <p>
              验证码已发送至 {phoneChangeRecovery.maskedNewPhone}
              。这一步验证新手机号确实由本人持有。
            </p>
            {simulation ? <p>内部模拟验证码已自动填入；真实短信渠道尚未验证。</p> : null}
            <label>
              新手机号收到的 6 位验证码
              <input
                name="workforce-phone-change-new-phone-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={newPhoneOtpCode}
                maxLength={6}
                disabled={busy !== null}
                onChange={(event) =>
                  setNewPhoneOtpCode(event.currentTarget.value.replace(/\D/g, ""))
                }
              />
            </label>
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "PHONE_CHANGE_NEW_PHONE_VERIFY" ? "正在验证新手机号…" : "验证新手机号"}
            </button>
          </form>
        ) : null}

        {status === "OTP_VERIFIED" && !phoneChange && recovery?.wechatChallengeId && inspection ? (
          <form
            name="workforce-invitation-wechat"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              const fixedCommand =
                unknownCommand?.kind === "WECHAT"
                  ? unknownCommand
                  : { kind: "WECHAT" as const, authorizationCode };
              if (fixedCommand.authorizationCode.length < 8) return;
              void runOnce("WECHAT", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                const result = await runInvitationCommand(fixedCommand, () =>
                  bindWorkforceInvitationWechat(
                    {
                      activationToken: recovery.activationToken,
                      challengeId: recovery.wechatChallengeId as string,
                      authorizationCode: fixedCommand.authorizationCode,
                      idempotencyKey: recovery.wechatIdempotencyKey,
                    },
                    inspection.invitationId,
                  ),
                );
                setStatus(result.status);
              });
            }}
          >
            <h2>{passwordReset ? "重新确认微信" : wechatRebind ? "绑定新微信" : "绑定微信"}</h2>
            <p>
              {simulation
                ? passwordReset
                  ? "当前使用内部模拟授权重新确认微信，不会连接或修改真实微信账号。"
                  : wechatRebind
                    ? "当前使用内部模拟授权验证换绑流程，不会连接或修改真实微信账号。"
                    : "当前使用内部模拟授权，不会连接或修改真实微信账号。"
                : passwordReset
                  ? "请重新完成微信授权，确认这是账号本人操作。"
                  : wechatRebind
                    ? "请使用准备绑定的新微信完成授权，确认后再完成换绑。"
                    : "请完成微信授权后继续。"}
            </p>
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "WECHAT"
                ? passwordReset
                  ? "正在确认…"
                  : wechatRebind
                    ? "正在验证新微信…"
                    : "正在绑定…"
                : unknownCommand?.kind === "WECHAT"
                  ? "继续确认原微信绑定"
                  : passwordReset
                    ? simulation
                      ? "完成模拟微信确认"
                      : "重新确认微信"
                    : wechatRebind
                      ? simulation
                        ? "完成模拟新微信验证"
                        : "验证新微信"
                      : simulation
                        ? "完成模拟微信绑定"
                        : "绑定微信"}
            </button>
          </form>
        ) : null}

        {(status === "WECHAT_BOUND" || (status === "ACTIVE" && localCompletionRequired)) &&
        wechatRebind &&
        recovery &&
        inspection ? (
          <form
            name="workforce-wechat-rebind-complete"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              void runOnce("WECHAT_REBIND", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                const result = await completeWorkforceWechatRebind({
                  activationToken: recovery.activationToken,
                  idempotencyKey: recovery.activationIdempotencyKey,
                });
                if (
                  result.purpose !== "WECHAT_REBIND" ||
                  result.invitationId !== inspection.invitationId ||
                  result.displayName !== inspection.displayName ||
                  result.evidence !== inspection.evidence
                ) {
                  throw new ApiRequestError(
                    "GATEWAY_RESPONSE_INVALID",
                    "微信换绑完成回执与当前链接或员工不一致，页面已保留原请求",
                  );
                }
                clearWorkforceInvitationActivationRecovery(window.sessionStorage);
                setRecovery(null);
                setWechatRebindResult(result);
                setLocalCompletionRequired(false);
                setStatus(result.status);
              });
            }}
          >
            <h2>确认替换原微信</h2>
            {localCompletionRequired ? (
              <output className="warning-band">
                云端已确认这次换绑，但本店尚未完成微信身份更新和旧设备退出。请继续原操作；完成前旧微信或旧设备可能仍可使用。
              </output>
            ) : null}
            <p>
              新微信已经通过本次验证。点击后会替换原微信，并让所有旧设备会话失效；不会修改密码、资金、提成或业务历史。
            </p>
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "WECHAT_REBIND" ? "正在完成换绑…" : "确认完成换绑"}
            </button>
          </form>
        ) : null}

        {(status === "PHONE_CHANGE_READY" || (status === "ACTIVE" && localCompletionRequired)) &&
        phoneChange &&
        recovery &&
        inspection ? (
          <form
            name="workforce-phone-change-complete"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              void runOnce("PHONE_CHANGE_COMPLETE", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                if (phoneChangeRecovery) {
                  ensureWorkforcePhoneChangeActivationRecovery(
                    window.sessionStorage,
                    phoneChangeRecovery,
                  );
                }
                const result = await completeWorkforcePhoneChange({
                  activationToken: recovery.activationToken,
                  idempotencyKey: recovery.activationIdempotencyKey,
                });
                if (
                  result.purpose !== "PHONE_CHANGE" ||
                  result.invitationId !== inspection.invitationId ||
                  result.displayName !== inspection.displayName ||
                  result.evidence !== inspection.evidence ||
                  (phoneChangeRecovery?.maskedNewPhone !== null &&
                    phoneChangeRecovery?.maskedNewPhone !== undefined &&
                    result.maskedNewPhone !== phoneChangeRecovery.maskedNewPhone)
                ) {
                  throw new ApiRequestError(
                    "GATEWAY_RESPONSE_INVALID",
                    "手机号更换完成回执与当前链接或员工不一致，页面已保留原请求",
                  );
                }
                clearWorkforceInvitationActivationRecovery(window.sessionStorage);
                clearWorkforcePhoneChangeActivationRecovery(window.sessionStorage);
                setRecovery(null);
                setPhoneChangeRecovery(null);
                setPhoneChangeResult(result);
                setLocalCompletionRequired(false);
                setStatus(result.status);
              });
            }}
          >
            <h2>确认更换登录手机号</h2>
            {localCompletionRequired ? (
              <output className="warning-band">
                云端已确认这次更换，但本店尚未完成登录手机号更新和旧设备退出。请继续原操作；完成前旧手机号或旧设备可能仍可使用。
              </output>
            ) : null}
            <p>
              当前手机号和新手机号都已验证。确认后，新登录手机号为
              {phoneChangeRecovery?.maskedNewPhone ?? "刚刚验证的新号码"}
              ，所有旧设备都会退出；会员余额、订单、提成和历史记录保持不变。
            </p>
            <button className="button primary" type="submit" disabled={busy !== null}>
              {busy === "PHONE_CHANGE_COMPLETE" ? "正在完成更换…" : "确认更换登录手机号"}
            </button>
          </form>
        ) : null}

        {!wechatRebind &&
        !phoneChange &&
        (status === "WECHAT_BOUND" || (status === "ACTIVE" && localCompletionRequired)) &&
        recovery &&
        inspection ? (
          <form
            name="workforce-invitation-activate"
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              const fixedCommand =
                unknownCommand?.kind === "ACTIVATE"
                  ? unknownCommand
                  : { kind: "ACTIVATE" as const, backupPassword };
              if (
                fixedCommand.backupPassword.length < 12 ||
                fixedCommand.backupPassword !== backupPasswordConfirm
              ) {
                return;
              }
              void runOnce("ACTIVATE", async () => {
                ensureWorkforceInvitationActivationRecovery(window.sessionStorage, recovery);
                persist(recovery, { activationSubmitted: true });
                const result = await runInvitationCommand(fixedCommand, () =>
                  activateWorkforceInvitation(
                    {
                      activationToken: recovery.activationToken,
                      backupPassword: fixedCommand.backupPassword,
                      idempotencyKey: recovery.activationIdempotencyKey,
                    },
                    { ...inspection, status },
                  ),
                );
                const expectedPurpose = inspection.purpose ?? "ACCOUNT_ACTIVATION";
                const resultPurpose = result.purpose ?? "ACCOUNT_ACTIVATION";
                if (resultPurpose !== expectedPurpose) {
                  throw new ApiRequestError(
                    "GATEWAY_RESPONSE_INVALID",
                    passwordReset
                      ? "密码恢复回执与当前恢复链接不一致，页面已保留原请求"
                      : "账号启用回执与当前邀请不一致，页面已保留原请求",
                  );
                }
                clearWorkforceInvitationActivationRecovery(window.sessionStorage);
                setRecovery(null);
                setBackupPassword("");
                setBackupPasswordConfirm("");
                setBackupPasswordVisible(false);
                setBackupPasswordConfirmVisible(false);
                setActivation(result);
                setLocalCompletionRequired(false);
                setStatus(result.status);
              });
            }}
          >
            <h2>{passwordReset ? "设置新密码" : "设置备用密码"}</h2>
            {localCompletionRequired ? (
              <output className="warning-band">
                {passwordReset
                  ? "云端已确认这次恢复，但本店尚未完成换密和旧设备退出。请重新输入刚才设置的同一新密码并继续；完成前旧密码可能仍可使用。"
                  : "云端已确认新账号，但本店尚未完成登录开通，当前不能当作已可登录。请用上次提交的同一备用密码继续确认原操作；如果反复提示账号占用，请停止重试并让老板核对本店账号，不要另发邀请。"}
              </output>
            ) : null}
            {!localCompletionRequired && recovery.activationSubmitted ? (
              <output className="warning-band">
                {passwordReset
                  ? "上次密码重置结果还需核对。请只输入上次提交的同一新密码，继续确认原密码重置；不要设置另一密码或另开操作。"
                  : "上次启用结果还需核对。请只输入上次提交的同一备用密码，继续确认原账号启用；不要设置另一密码或另开操作。"}
              </output>
            ) : null}
            <p>
              {passwordReset
                ? "至少 12 位，不要与其他网站或 App 共用。重置成功后请用新密码登录。"
                : "用于微信暂时不可用时登录。至少 12 位，不要与其他网站或 App 共用。"}
            </p>
            <label htmlFor={backupPasswordId}>{passwordReset ? "新密码" : "备用密码"}</label>
            <div className="inline-actions">
              <input
                id={backupPasswordId}
                name="invitation-backup-password"
                type={backupPasswordVisible ? "text" : "password"}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                value={backupPassword}
                disabled={busy !== null || unknownCommand?.kind === "ACTIVATE"}
                onChange={(event) => setBackupPassword(event.currentTarget.value)}
              />
              <button
                className="button secondary"
                type="button"
                aria-label={
                  backupPasswordVisible
                    ? passwordReset
                      ? "隐藏新密码"
                      : "隐藏备用密码"
                    : passwordReset
                      ? "显示新密码"
                      : "显示备用密码"
                }
                aria-pressed={backupPasswordVisible}
                disabled={busy !== null}
                onClick={() => setBackupPasswordVisible((visible) => !visible)}
              >
                {backupPasswordVisible ? "隐藏" : "显示"}
              </button>
            </div>
            <label htmlFor={backupPasswordConfirmId}>
              {passwordReset ? "再次输入新密码" : "再输入一次备用密码"}
            </label>
            <div className="inline-actions">
              <input
                id={backupPasswordConfirmId}
                name="invitation-backup-password-confirm"
                type={backupPasswordConfirmVisible ? "text" : "password"}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                value={backupPasswordConfirm}
                disabled={busy !== null || unknownCommand?.kind === "ACTIVATE"}
                onChange={(event) => setBackupPasswordConfirm(event.currentTarget.value)}
              />
              <button
                className="button secondary"
                type="button"
                aria-label={
                  backupPasswordConfirmVisible
                    ? passwordReset
                      ? "隐藏确认密码"
                      : "隐藏备用密码确认"
                    : passwordReset
                      ? "显示确认密码"
                      : "显示备用密码确认"
                }
                aria-pressed={backupPasswordConfirmVisible}
                disabled={busy !== null}
                onClick={() => setBackupPasswordConfirmVisible((visible) => !visible)}
              >
                {backupPasswordConfirmVisible ? "隐藏" : "显示"}
              </button>
            </div>
            {backupPasswordConfirm && backupPassword !== backupPasswordConfirm ? (
              <div className="form-hint">
                {passwordReset ? "两次输入的新密码不一致" : "两次输入的备用密码不一致"}
              </div>
            ) : null}
            <button
              className="button primary"
              type="submit"
              disabled={
                busy !== null ||
                (unknownCommand?.kind !== "ACTIVATE" &&
                  (backupPassword.length < 12 || backupPassword !== backupPasswordConfirm))
              }
            >
              {busy === "ACTIVATE"
                ? passwordReset
                  ? "正在重置…"
                  : "正在启用…"
                : unknownCommand?.kind === "ACTIVATE"
                  ? passwordReset
                    ? "继续确认原密码重置"
                    : "继续确认原账号启用"
                  : recovery.activationSubmitted
                    ? passwordReset
                      ? "继续确认原密码重置"
                      : "继续确认原账号启用"
                    : passwordReset
                      ? "重置我的密码"
                      : "启用我的账号"}
            </button>
          </form>
        ) : null}

        {status === "ACTIVE" && !localCompletionRequired ? (
          <div className="success-band" aria-live="polite">
            <strong>
              {passwordReset
                ? "密码已重置"
                : wechatRebind
                  ? "微信已换绑"
                  : phoneChange
                    ? "登录手机号已更换"
                    : activation
                      ? "账号已启用"
                      : "邀请已完成"}
            </strong>
            {wechatRebind ? (
              <p>
                {wechatRebindResult
                  ? `新微信：${wechatRebindResult.maskedWechat}。`
                  : "这次微信换绑已经完成。"}
                所有旧设备会话均已失效，请在每台设备上重新登录。账号密码、资金、提成和业务历史保持不变。
                {simulation ? " 本次仅为内部模拟流程验证，未连接或修改真实微信账号。" : ""}
              </p>
            ) : phoneChange ? (
              <p>
                新登录手机号：{phoneChangeResult?.maskedNewPhone ?? "刚刚验证的新号码"}
                。所有旧设备已退出，请用新手机号重新登录。会员余额、订单、提成和历史记录均未转移或删除。
                {simulation ? " 本次仅为内部模拟流程验证，未发送真实短信。" : ""}
              </p>
            ) : passwordReset ? (
              <p>
                {activation ? `登录名：${activation.login}。` : "这次密码恢复已经完成。"}
                {activation?.revokedSessionCount !== undefined
                  ? ` 已退出 ${activation.revokedSessionCount} 个旧设备会话。`
                  : " 所有旧设备会话已按安全规则退出。"}
                账号资金、提成和业务历史保持不变。现在请用新密码登录。
                {simulation ? " 本次仅为内部模拟流程验证。" : ""}
              </p>
            ) : (
              <p>
                {activation ? `登录名：${activation.login}。` : "这份邀请已经完成。"}
                {inspection?.roles.includes("COACH")
                  ? " 这是新开通的助教账号；如有旧的停用账号，旧账号不会自动恢复。"
                  : ""}
                {simulation
                  ? " 本次仅为内部模拟流程验证。"
                  : activation
                    ? " 现在可以返回员工 App 登录。"
                    : " 此处是原邀请的完成记录，不代表账号目前仍可登录；如账号后来停用，请由老板按独立流程处理。"}
              </p>
            )}
          </div>
        ) : null}
      </section>
    </main>
  );
}
